import { useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Grid,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';

import { Icon } from '../../shared/Icon';
import { GiftDetailDialog } from './GiftDetailDialog';
import { GiftScheduleMonthCell } from './GiftScheduleMonthCell';

import type { GameElementDto } from '../../hooks/gameElements';
import { useGameElements } from '../../hooks/gameElements';
import type { GiftSchemas } from '../../hooks/useGiftApi';
import { useGiftApi } from '../../hooks/useGiftApi';
import type { JiraSubtask } from '../../hooks/useJiraApi';
import { useJiraIssues } from '../../hooks/useJiraApi';

import { canScheduleSubtask, findSubtaskForGift } from '../domain/giftPlan';
import {
    createGiftSchedule,
    createGiftScheduleYear,
    ScheduledGiftOccurrence,
} from '../domain/giftSchedule';

const MONTH_INDICES = Array.from({ length: 12 }, (_, index) => index);

type Gift = GiftSchemas['FullGiftAdminItem'];
type SelectedScheduleItem =
    | {
          kind: 'scheduled';
          gift: Gift;
          occurrence: ScheduledGiftOccurrence;
          subtask: JiraSubtask | null;
      }
    | { kind: 'planned'; subtask: JiraSubtask };

export const GiftScheduleCalendar = () => {
    const gameElementsQuery = useGameElements();
    const gameElements = useMemo(() => {
        if (!gameElementsQuery.data) return undefined;
        return new Map<string, GameElementDto>(
            gameElementsQuery.data.gameElements.map((element) => [
                element.id,
                element,
            ]),
        );
    }, [gameElementsQuery.data]);
    const giftApi = useGiftApi();
    const giftsQuery = giftApi.useQuery('get', '/gift/api/v2/admin/gifts');
    const openIssuesQuery = useJiraIssues('open');
    const doneIssuesQuery = useJiraIssues('done');
    const gifts = giftsQuery.data?.gifts;
    const issues = useMemo(
        () => [
            ...(openIssuesQuery.data ?? []),
            ...(doneIssuesQuery.data ?? []),
        ],
        [openIssuesQuery.data, doneIssuesQuery.data],
    );
    const schedule = useMemo(
        () => createGiftSchedule(gifts, issues),
        [gifts, issues],
    );
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const activeYear =
        selectedYear !== null && schedule.years.includes(selectedYear)
            ? selectedYear
            : (schedule.years[0] ?? new Date().getFullYear());
    const scheduleYear = useMemo(
        () => createGiftScheduleYear(gifts, schedule.plannedGifts, activeYear),
        [gifts, schedule.plannedGifts, activeYear],
    );
    const [selected, setSelected] = useState<SelectedScheduleItem | null>(null);

    const handleOnSelectGift = useCallback(
        (occurrence: ScheduledGiftOccurrence) => {
            const gift = occurrence.gift;
            setSelected({
                kind: 'scheduled',
                gift,
                occurrence,
                subtask: findSubtaskForGift(schedule.allSubtasks, gift),
            });
        },
        [schedule.allSubtasks],
    );

    const handleOnSelectPlanned = useCallback(
        (subtask: JiraSubtask) => setSelected({ kind: 'planned', subtask }),
        [],
    );

    if (gameElementsQuery.isPending || giftsQuery.isPending) {
        return (
            <Stack sx={{ alignItems: 'center', gap: 2, py: 6 }}>
                <CircularProgress />
                <Typography color="text.secondary">
                    Loading schedule...
                </Typography>
            </Stack>
        );
    }

    if (gameElementsQuery.isError || giftsQuery.isError || !gameElements) {
        return (
            <Alert severity="error">
                Unable to load the gift schedule. Please try again.
            </Alert>
        );
    }

    if (schedule.years.length === 0) {
        return (
            <Paper
                variant="outlined"
                sx={{
                    p: 6,
                    textAlign: 'center',
                    color: 'text.secondary',
                    borderRadius: 2,
                    borderStyle: 'dashed',
                }}
            >
                <Box
                    sx={{
                        fontSize: 48,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Icon name="calendar_month" />
                </Box>
                <Typography sx={{ mt: 1 }}>
                    No gifts scheduled or planned yet
                </Typography>
            </Paper>
        );
    }

    const selectedGift = selected?.kind === 'scheduled' ? selected.gift : null;
    const selectedSubtask = selected?.subtask ?? null;

    return (
        <Stack spacing={2}>
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Tabs
                    value={activeYear}
                    onChange={(_, year: number) => setSelectedYear(year)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {schedule.years.map((year) => (
                        <Tab
                            key={year}
                            value={year}
                            label={year}
                            sx={{ fontWeight: 600, minWidth: 72 }}
                        />
                    ))}
                </Tabs>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                >
                    <Typography color="primary" variant="subtitle1">
                        {`${scheduleYear.totalGifts} gift${scheduleYear.totalGifts === 1 ? '' : 's'} in ${activeYear}`}
                    </Typography>
                    {scheduleYear.totalPlanned > 0 && (
                        <Typography color="warning" variant="subtitle1">
                            {`${scheduleYear.totalPlanned} planned in ${activeYear}`}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <Grid container spacing={1.5}>
                {MONTH_INDICES.map((month) => (
                    <Grid key={month} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <GiftScheduleMonthCell
                            year={activeYear}
                            month={month}
                            gifts={scheduleYear.giftsByMonth[month] ?? []}
                            plannedGifts={
                                scheduleYear.plannedByMonth[month] ?? []
                            }
                            gameElements={gameElements}
                            onSelectGift={handleOnSelectGift}
                            onSelectPlanned={handleOnSelectPlanned}
                        />
                    </Grid>
                ))}
            </Grid>
            <GiftDetailDialog
                open={selected !== null}
                onClose={() => setSelected(null)}
                gift={selectedGift}
                selectedPublication={
                    selected?.kind === 'scheduled' ? selected.occurrence : null
                }
                subtask={selectedSubtask}
                initialTab={
                    selected?.kind === 'planned' ? 'planned' : 'scheduled'
                }
                canSchedule={
                    selectedSubtask
                        ? canScheduleSubtask(selectedSubtask)
                        : false
                }
            />
        </Stack>
    );
};
